import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Activity,
  Radio,
  Target,
  TrendingUp,
  Wifi,
  WifiOff,
  RefreshCw,
  Play,
  Pause
} from 'lucide-react';
import { useLiveShots } from '../hooks/useShots';
import { useLiveSessionMonitor } from '../hooks/useRealtimeShots';
import { ShotData } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface LiveShotMonitorProps {
  sessionId?: string;
  className?: string;
  onShotSelect?: (shot: ShotData) => void;
}

export const LiveShotMonitor: React.FC<LiveShotMonitorProps> = ({
  sessionId,
  className,
  onShotSelect
}) => {
  const [isLive, setIsLive] = useState(true);
  const [selectedShot, setSelectedShot] = useState<ShotData | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('connecting');

  // Fetch live shots with polling/WebSocket
  const { data: shots = [], isLoading, error, refetch: _refetch } = useLiveShots(sessionId, isLive);

  // WebSocket integration for real-time updates
  const { emitShot: _emitShot, requestAnalysis: _requestAnalysis } = useLiveSessionMonitor(sessionId);

  // Update connection status
  useEffect(() => {
    if (error) {
      setConnectionStatus('disconnected');
    } else if (isLoading) {
      setConnectionStatus('connecting');
    } else {
      setConnectionStatus('connected');
    }
  }, [isLoading, error]);

  const handleShotClick = (shot: ShotData) => {
    setSelectedShot(shot);
    if (onShotSelect) {
      onShotSelect(shot);
    }
  };

  const toggleLiveMode = () => {
    setIsLive(!isLive);
  };

  const getQualityColor = (quality: string) => {
    switch (quality?.toUpperCase()) {
      case 'EXCELLENT':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'GOOD':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'AVERAGE':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'POOR':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const ConnectionIndicator = () => {
    const statusConfig = {
      connected: { icon: Wifi, color: 'text-green-500', pulse: true },
      disconnected: { icon: WifiOff, color: 'text-red-500', pulse: false },
      connecting: { icon: RefreshCw, color: 'text-yellow-500', pulse: true }
    };

    const config = statusConfig[connectionStatus];
    const Icon = config.icon;

    return (
      <div className="flex items-center gap-2">
        <div className="relative">
          <Icon className={cn('h-4 w-4', config.color)} />
          {config.pulse && (
            <span className={cn(
              'absolute inset-0 rounded-full opacity-75 animate-ping',
              connectionStatus === 'connected' ? 'bg-green-500' : 'bg-yellow-500'
            )} />
          )}
        </div>
        <span className="text-sm text-muted-foreground capitalize">
          {connectionStatus}
        </span>
      </div>
    );
  };

  const LiveStats = () => {
    const stats = {
      totalShots: shots.length,
      excellentShots: shots.filter(s => s.quality === 'excellent').length,
      averageDistance: shots.reduce((acc, s) => acc + (s.distance || 0), 0) / (shots.length || 1),
      averagePower: shots.reduce((acc, s) => acc + (s.powerTransfer || 0), 0) / (shots.length || 1),
    };

    return (
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Total Shots</p>
          <p className="text-2xl font-bold">{stats.totalShots}</p>
        </div>
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Excellent Shots</p>
          <p className="text-2xl font-bold text-green-600">{stats.excellentShots}</p>
        </div>
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Avg Distance</p>
          <p className="text-lg font-semibold">{stats.averageDistance.toFixed(1)}m</p>
        </div>
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Avg Power</p>
          <p className="text-lg font-semibold">{(stats.averagePower * 100).toFixed(0)}%</p>
        </div>
      </div>
    );
  };

  return (
    <Card className={cn('h-full flex flex-col', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Radio className="h-5 w-5" />
              Live Shot Monitor
            </CardTitle>
            <CardDescription>Real-time shot tracking and analysis</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <ConnectionIndicator />
            <Button
              variant={isLive ? 'default' : 'outline'}
              size="sm"
              onClick={toggleLiveMode}
              className="gap-2"
            >
              {isLive ? (
                <>
                  <Pause className="h-4 w-4" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Resume
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col">
        <LiveStats />
        
        <Separator className="mb-4" />
        
        <div className="flex-1 overflow-hidden">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Recent Shots</h3>
            <Badge variant="outline" className="gap-1">
              <Activity className="h-3 w-3" />
              {shots.length} shots
            </Badge>
          </div>
          
          <ScrollArea className="h-[400px] pr-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : shots.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <Target className="h-8 w-8 mb-2" />
                <p className="text-sm">No shots recorded yet</p>
                <p className="text-xs">Shots will appear here in real-time</p>
              </div>
            ) : (
              <div className="space-y-2">
                {shots.map((shot, index) => (
                  <div
                    key={shot.id || index}
                    onClick={() => handleShotClick(shot)}
                    className={cn(
                      'p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md',
                      selectedShot?.id === shot.id ? 'ring-2 ring-primary' : '',
                      getQualityColor(shot.quality || '')
                    )}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          #{shots.length - index}
                        </Badge>
                        <span className="text-sm font-medium">
                          {shot.shotType || 'Shot'}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {shot.timestamp ? formatDistanceToNow(new Date(shot.timestamp), { addSuffix: true }) : 'Just now'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Distance:</span>
                        <span className="ml-1 font-medium">{shot.distance?.toFixed(1)}m</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Speed:</span>
                        <span className="ml-1 font-medium">{shot.exitVelocity?.toFixed(0)}m/s</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Power:</span>
                        <span className="ml-1 font-medium">{((shot.powerTransfer || 0) * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex gap-1">
                        {shot.timing && (
                          <Badge variant="secondary" className="text-xs">
                            Timing: {shot.timing}
                          </Badge>
                        )}
                        {shot.footwork && (
                          <Badge variant="secondary" className="text-xs">
                            Footwork: {shot.footwork}
                          </Badge>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs gap-1">
                        <TrendingUp className="h-3 w-3" />
                        {shot.quality}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
};